import React from "react";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { FormSectionElements, FormSections, FormSubSections } from "../../entities/formTemplateDataType";
import { getRootComponent } from "../../utilities/ElementUtils";

interface SearchFilterOptions {
    label: string;
    value: string | null;
}

export const getSummaryListColumnOptions = (
    element: FormSectionElements,
    isEng: boolean
): SearchFilterOptions[] => {
    const options: SearchFilterOptions[] = [{
        label: isEng ? 'No summary list filter' : 'Pas de filtre summary list',
        value: null
    }];

    const root = getRootComponent(element);
    const sections = root?.ListOfFormSections ?? [];

    sections.forEach((section: FormSections) => {
        section?.ListOfFormSubSections?.forEach((subSection: FormSubSections) => {
            subSection?.ListOfFormSectionElements?.forEach((el: FormSectionElements) => {
                if ((el?.Datatype === "SummaryList" || el?.Datatype === "PagedSummaryList") && el.subSectionList?.length) {
                    const summaryTitle = isEng ? el.ENUTitle : el.FRATitle;
                    const summaryId = el.Id;

                    options.push({
                        label: `${summaryTitle || summaryId || 'Summary List'} - (Custom column)`,
                        value: `${summaryId}|__CUSTOM__`
                    });

                    const columns = el.subSectionList[0]?.ListOfFormSectionElements ?? [];
                    columns.forEach((column: FormSectionElements) => {
                        const columnKey = column?.LoadBindName;
                        if (!columnKey) return;
                        const columnTitle = isEng ? column.ENUTitle : column.FRATitle;
                        options.push({
                            label: `${summaryTitle || summaryId || 'Summary List'} - ${columnTitle || columnKey}`,
                            value: `${summaryId}|${columnKey}`
                        });
                    });
                }
            });
        });
    });

    return options;
};

export const getSummaryListColumnOptionsForSelected = (
    element: FormSectionElements,
    isEng: boolean
): SearchFilterOptions[] => {
    const allOptions = getSummaryListColumnOptions(element, isEng);
    const selectedSummaryKey = (element as any)?.filterSummaryListId;
    if (!selectedSummaryKey) return allOptions;
    return allOptions.filter(opt => {
        if (!opt?.value) return true;
        return typeof opt.value === 'string' && opt.value.startsWith(`${selectedSummaryKey}|`);
    });
};

export const renderSearchFilterCheckbox = (
    element: FormSectionElements,
    enableSearchFilter: boolean,
    onChangeCallback: (checked: boolean) => void,
    label: string = "Enable Search Filter:"
): JSX.Element => {
    return (
        <div style={{ width: '100%', display: "flex", alignItems: 'center', marginTop: '10px' }}>
            <div style={{ width: '25%' }}>
                <label htmlFor={`enable_search_button_${element.Sequence}`}>
                    {label}
                </label>
            </div>
            <div style={{ width: '60%' }}>
                <Checkbox
                    id={`enable_search_button_${element.Sequence}`}
                    checked={enableSearchFilter}
                    onChange={(e) => onChangeCallback(e.checked)}
                />
            </div>
        </div>
    );
};

export const renderSearchFilterFields = (
    element: FormSectionElements,
    isEng: boolean,
    updateCallback: () => void
): JSX.Element => {
    const elementAny = element as any;
    
    return (
        <>
            <div style={{ display: "flex", marginTop: '10px' }}>
                <div style={{ width: '25%' }}>
                    <label htmlFor={`summary_list_filter_${element.Sequence}`}>
                        Summary list filter
                    </label>
                </div>
                <div style={{ width: '60%' }}>
                    <MultiSelect
                        id={`summary_list_filter_${element.Sequence}`}
                        className="ontario-dropdown-small"
                        optionLabel="label"
                        optionValue="value"
                        options={getSummaryListColumnOptionsForSelected(element, isEng)}
                        value={element.filterSummaryListId
                            ? (Array.isArray(elementAny.filterSummaryListColumns) && elementAny.filterSummaryListColumns.length
                                ? elementAny.filterSummaryListColumns.map((col: string) => `${element.filterSummaryListId}|${col}`)
                                : (element.filterSummaryListColumn ? [`${element.filterSummaryListId}|${element.filterSummaryListColumn}`] : []))
                            : []}
                        placeholder="Select summary list columns"
                        style={{ width: '100%' }}
                        display="chip"
                        onChange={(e) => {
                            const rawValues = e.value ?? [];
                            const selectedValues: string[] = (Array.isArray(rawValues) ? rawValues : [rawValues]).filter(v => typeof v === 'string');
                            if (!selectedValues.length) {
                                elementAny.filterSummaryListId = null;
                                elementAny.filterSummaryListColumn = null;
                                elementAny.filterSummaryListColumns = [];
                                updateCallback();
                                return;
                            }
                            const [firstSummaryId] = selectedValues[0].split('|');
                            const scopedValues = selectedValues.filter(v => v.startsWith(`${firstSummaryId}|`));
                            const columnNames = scopedValues.map(val => val.split('|')[1]).filter(Boolean);
                            elementAny.filterSummaryListId = firstSummaryId;
                            elementAny.filterSummaryListColumns = columnNames;
                            elementAny.filterSummaryListColumn = columnNames[0] ?? null;
                            updateCallback();
                        }}
                    />
                </div>
            </div>

            {element.filterSummaryListId && (
                <div style={{ display: "flex", marginTop: '10px' }}>
                    <div style={{ width: '25%' }}>
                        <label htmlFor={`custom_filter_column_${element.Sequence}`}>
                            Custom filter column
                        </label>
                    </div>
                    <div style={{ width: '60%' }}>
                        <InputText
                            className="ontario-input"
                            id={`custom_filter_column_${element.Sequence}`}
                            placeholder="e.g., receivedDate (from API response)"
                            value={element.filterSummaryListColumn ?? ''}
                            onChange={(e) => {
                                elementAny.filterSummaryListColumn = e.target.value;
                                elementAny.filterSummaryListColumns = elementAny.filterSummaryListColumn ? [elementAny.filterSummaryListColumn] : [];
                                updateCallback();
                            }}
                        />
                        <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
                            Enter any field name from the API response data
                        </small>
                    </div>
                </div>
            )}
        </>
    );
};

export const getPagedSummaryListOptions = (
    element: FormSectionElements,
    isEng: boolean
): SearchFilterOptions[] => {
    const options: SearchFilterOptions[] = [{
        label: isEng ? 'No search target' : 'Pas de cible de recherche',
        value: null
    }];

    const root = getRootComponent(element);
    const sections = root?.ListOfFormSections ?? [];

    sections.forEach((section: FormSections) => {
        section?.ListOfFormSubSections?.forEach((subSection: FormSubSections) => {
            subSection?.ListOfFormSectionElements?.forEach((el: FormSectionElements) => {
                if (el?.Datatype === "PagedSummaryList") {
                    const summaryTitle = isEng ? el.ENUTitle : el.FRATitle;
                    const summaryId = el.Id;
                    options.push({
                        label: summaryTitle || summaryId || 'Paged Summary List',
                        value: summaryId
                    });
                }
            });
        });
    });

    return options;
};

export const renderSearchButtonConfig = (
    element: FormSectionElements,
    enableSearch: boolean,
    onChangeCallback: (checked: boolean) => void,
    isEng: boolean,
    updateCallback: () => void,
    label: string = "Enable Search Button:"
): JSX.Element => {
    const elementAny = element as any;
    
    return (
        <>
            <div style={{ width: '100%', display: "flex", alignItems: 'center', marginTop: '10px' }}>
                <div style={{ width: '25%' }}>
                    <label htmlFor={`enable_search_button_${element.Sequence}`}>
                        {label}
                    </label>
                </div>
                <div style={{ width: '60%' }}>
                    <Checkbox
                        id={`enable_search_button_${element.Sequence}`}
                        checked={enableSearch}
                        onChange={(e) => onChangeCallback(e.checked)}
                    />
                </div>
            </div>
            
            {enableSearch && (
                <div style={{ display: "flex", marginTop: '10px' }}>
                    <div style={{ width: '25%' }}>
                        <label htmlFor={`search_target_${element.Sequence}`}>
                            {isEng ? 'Search Target (Paged Summary List):' : 'Cible de recherche (Liste paginée):'}
                        </label>
                    </div>
                    <div style={{ width: '60%' }}>
                        <Dropdown
                            id={`search_target_${element.Sequence}`}
                            className="ontario-dropdown-small"
                            optionLabel="label"
                            optionValue="value"
                            options={getPagedSummaryListOptions(element, isEng)}
                            value={elementAny.searchTargetSummaryListId ?? null}
                            placeholder={isEng ? "Select paged summary list" : "Sélectionner la liste paginée"}
                            style={{ width: '100%' }}
                            onChange={(e) => {
                                elementAny.searchTargetSummaryListId = e.value;
                                updateCallback();
                            }}
                        />
                    </div>
                </div>
            )}
        </>
    );
};
